import * as React from 'react';
import { useIntl } from 'react-intl';
import ReactDOM from 'react-dom';
import PermissionsService, { PERMISSION_PERIOD_LOCK } from '../../../Services/PermissionsService';
import { PeriodLockDefinitionRecord } from '../interfaces';
import { putRequest } from '../../../Services/HttpService';
import LoaderService from '../../../Services/LoaderService';
import useAnalytics from '../../../hooks/useAnalytics';
import { getTableName } from '../../CmdTable/CmdTableService';
import { PERIOD_LOCK_DEFINITION_CUSTOMERS } from '../constants';

interface IActionMenuProps<T extends PeriodLockDefinitionRecord> {
  data: T;
  showDetailModalCallback: React.Dispatch<React.SetStateAction<boolean>>;
  showDiscardConfirmModalCallback: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  closeCallback: () => void;
  position: { top: number; left: number };
  updateTable: () => void;
}

const ActionsMenu = React.forwardRef<HTMLDivElement, IActionMenuProps<PeriodLockDefinitionRecord>>(
  (props, ref) => {
    const { formatMessage: f } = useIntl();
    const { trackEvent } = useAnalytics();

    const handleActivate = async (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      event.preventDefault();
      trackEvent([`table ${getTableName(PERIOD_LOCK_DEFINITION_CUSTOMERS)}`, 'click', 'Action activate']);
      props.closeCallback();
      LoaderService.showLoader();
      const response = await putRequest(`/v1/period-lock-definition/${props.data.id}/enabled`, {
        enabled: true,
      });
      LoaderService.showLoader(false);
      props.updateTable();
    };

    const handleDeactivate = async (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      event.preventDefault();
      trackEvent([`table ${getTableName(PERIOD_LOCK_DEFINITION_CUSTOMERS)}`, 'click', 'Action deactivate']);
      props.closeCallback();
      LoaderService.showLoader();
      const response = await putRequest(`/v1/period-lock-definition/${props.data.id}/enabled`, {
        enabled: false,
      });
      LoaderService.showLoader(false);
      props.updateTable();
    };

    const handleDetail = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      event.preventDefault();
      trackEvent([`table ${getTableName(PERIOD_LOCK_DEFINITION_CUSTOMERS)}`, 'click', 'Action open detail']);
      props.showDetailModalCallback(true);
    };

    const menu = (
      <div
        className="ridebook-dropdown-menu dropdown-menu dropdown-menu-right dropdown-menu-custom fs-2"
        style={{
          display: 'block',
          width: '160px',
          position: 'absolute',
          top: props.position.top,
          left: props.position.left,
          zIndex: 1,
        }}
        aria-labelledby="dropdown-actions-1"
        ref={ref}
      >
        {props.data && (
          <div>
            {PermissionsService.hasAccess(PERMISSION_PERIOD_LOCK, ['read']) && (
              <a
                data-cy="action-detail"
                href="#"
                title={f({ id: 'table_list.actions.detail' })}
                className="px-3 py-2 d-flex align-items-center text-decoration-none cmd-table-text-gray"
                onClick={handleDetail}
                id={`action-detail-${props.data.id}`}
              >
                <i className="cmd-icons cmd-icon-detail mr-2 fs-4" />
                {f({ id: 'table_list.actions.detail' })}
              </a>
            )}
            {PermissionsService.hasAccess(PERMISSION_PERIOD_LOCK, ['update']) && (
              <a
                data-cy={`action-${props.data.enabled ? 'deactivate' : 'activate'}`}
                href="#"
                title={f({
                  id: `table_list.actions.${props.data.enabled ? 'deactivate' : 'activate'}`,
                })}
                className="px-3 py-2 d-flex align-items-center text-decoration-none cmd-table-text-gray"
                id={`action-${props.data.enabled ? 'deactivate' : 'activate'}-${props.data.id}`}
                onClick={props.data.enabled ? handleDeactivate : handleActivate}
              >
                <i
                  className={`cmd-icons cmd-icon-${
                    props.data.enabled ? 'inactive' : 'active'
                  } mr-2 fs-4`}
                />
                {f({ id: `table_list.actions.${props.data.enabled ? 'deactivate' : 'activate'}` })}
              </a>
            )}
            {PermissionsService.hasAccess(PERMISSION_PERIOD_LOCK, ['update']) && (
              <a
                data-cy="action-discard"
                href="#"
                title={f({ id: 'table.deleteBtn' })}
                className="px-3 py-2 d-flex align-items-center text-decoration-none cmd-table-text-gray"
                id={`action-discard-${props.data.id}`}
                onClick={props.showDiscardConfirmModalCallback}
              >
                <i className="cmd-icons cmd-icon-bin mr-2 fs-4" /> {f({ id: 'table.deleteBtn' })}
              </a>
            )}
          </div>
        )}
      </div>
    );

    return ReactDOM.createPortal(menu, document.body);
  }
);

export default ActionsMenu;
